export function formatMoney (value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  return num.toLocaleString('ko-KR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

export function formatPointAmount (value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  return num.toLocaleString('ko-KR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

export function roundToDecimals (value, decimals = 2) {
  const num = Number(value)
  if (!Number.isFinite(num)) return 0
  const factor = 10 ** decimals
  return Math.round(num * factor) / factor
}

/** 금액·포인트 입력 필드용 (소수점 2자리, 음수 허용) */
export function formatDecimalInput (value, decimals = 2) {
  const num = roundToDecimals(value, decimals)
  if (!Number.isFinite(num) || num === 0) return ''
  const abs = Math.abs(num)
  const formatted = abs.toLocaleString('ko-KR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  })
  return num < 0 ? `-${formatted}` : formatted
}

function sanitizeSignedDecimalRaw (raw, maxDecimals = 2) {
  let trimmed = String(raw || '').trim().replace(/,/g, '')
  if (!trimmed) return ''

  const negative = trimmed.startsWith('-')
  if (negative) trimmed = trimmed.slice(1)
  if (!trimmed) return negative ? '-' : ''

  const parts = trimmed.split('.')
  const intPart = (parts[0] || '').replace(/\D/g, '')
  const hadDot = parts.length > 1 || trimmed.includes('.')
  let decPart = null
  if (hadDot) {
    decPart = parts.slice(1).join('').replace(/\D/g, '').slice(0, maxDecimals)
  }

  let body = intPart
  if (hadDot) {
    body = `${intPart}.${decPart != null ? decPart : ''}`
  }

  if (!body.replace(/\./g, '')) {
    if (hadDot) body = '0.'
    else return negative ? '-' : ''
  }

  return negative ? `-${body}` : body
}

function formatDecimalInputDisplay (sanitized, maxDecimals = 2) {
  if (!sanitized || sanitized === '-') return sanitized
  if (sanitized === '-.') return '-.'
  if (sanitized === '.') return '.'

  const negative = sanitized.startsWith('-')
  const body = negative ? sanitized.slice(1) : sanitized
  const parts = body.split('.')
  const intRaw = (parts[0] || '').replace(/\D/g, '')
  const intFormatted = intRaw
    ? Number(intRaw).toLocaleString('ko-KR')
    : '0'

  if (parts.length > 1 || body.includes('.')) {
    const dec = parts[1] != null ? parts[1].replace(/\D/g, '').slice(0, maxDecimals) : ''
    const display = body.endsWith('.') && !dec
      ? `${intFormatted}.`
      : `${intFormatted}.${dec}`
    return negative ? `-${display}` : display
  }

  return negative ? `-${intFormatted}` : intFormatted
}

/** 입력 중 표시용 — 콤마 없이 숫자·소수점만 허용 (최대 2자리) */
export function sanitizePlainDecimalInput (raw, maxDecimals = 2) {
  let s = String(raw || '').trim().replace(/,/g, '')
  if (!s) return ''

  const negative = s.startsWith('-')
  if (negative) s = s.slice(1)
  if (!s) return '-'

  const dotIndex = s.indexOf('.')
  let intPart = ''
  let decPart = ''
  let hasDot = false

  if (dotIndex === -1) {
    intPart = s.replace(/\D/g, '')
  } else {
    hasDot = true
    intPart = s.slice(0, dotIndex).replace(/\D/g, '')
    decPart = s.slice(dotIndex + 1).replace(/\D/g, '').slice(0, maxDecimals)
  }

  let body = intPart
  if (hasDot) {
    body = `${intPart}.${decPart}`
  }

  if (!body.replace(/\./g, '')) {
    if (hasDot) body = '0.'
    else return negative ? '-' : ''
  }

  return negative ? `-${body}` : body
}

export function parsePlainDecimalValue (raw, maxDecimals = 2) {
  const sanitized = sanitizePlainDecimalInput(raw, maxDecimals)
  if (!sanitized || sanitized === '-' || sanitized === '.' || sanitized === '-.') {
    return 0
  }
  const numeric = parseFloat(sanitized)
  return Number.isFinite(numeric) ? roundToDecimals(numeric, maxDecimals) : 0
}

/** 입력 문자열 → 소수 (최대 2자리, 선행 - 허용) */
export function parseSignedDecimalInput (raw, maxDecimals = 2) {
  const sanitized = sanitizeSignedDecimalRaw(raw, maxDecimals)
  if (!sanitized) return { value: 0, display: '' }
  if (sanitized === '-' || sanitized === '-.' || sanitized === '.') {
    return { value: 0, display: sanitized }
  }

  const endsWithDot = sanitized.endsWith('.')
  const numeric = parseFloat(sanitized.replace(/,/g, ''))
  const value = Number.isFinite(numeric) ? roundToDecimals(numeric, maxDecimals) : 0

  if (endsWithDot) {
    return { value, display: formatDecimalInputDisplay(sanitized, maxDecimals) }
  }

  return {
    value,
    display: value === 0 ? '' : formatDecimalInput(value, maxDecimals)
  }
}

/** 금액 입력 필드용 정수 표시 (콤마만, 소수점 없음, 음수 허용) */
export function formatIntegerInput (value) {
  const num = Number(value)
  if (!Number.isFinite(num) || num === 0) return ''
  const abs = Math.floor(Math.abs(num)).toLocaleString('ko-KR')
  return num < 0 ? `-${abs}` : abs
}

/** 입력 문자열 → 정수 (선행 - 허용, 입력 중 '-'만 있을 때 display '-' 유지) */
export function parseSignedIntegerInput (raw) {
  const trimmed = String(raw || '').trim()
  if (!trimmed) return { value: 0, display: '' }
  if (trimmed === '-') return { value: 0, display: '-' }

  const negative = trimmed.startsWith('-')
  const digits = trimmed.replace(/[^\d]/g, '')
  if (!digits) {
    return { value: 0, display: negative ? '-' : '' }
  }

  const value = negative ? -Number(digits) : Number(digits)
  return { value, display: formatIntegerInput(value) }
}

/** Fiat → USDT (rate = fiat units per 1 USDT) */
export function convertFiatToUsdt (fiatAmount, ratePerUsdt) {
  const amount = Number(fiatAmount)
  const rate = Number(ratePerUsdt)
  if (!Number.isFinite(amount) || amount <= 0) return null
  if (!Number.isFinite(rate) || rate <= 0) return null
  return amount / rate
}

/** @deprecated convertFiatToUsdt 사용 */
export function convertKrwToUsdt (krw, ratePerUsdt) {
  return convertFiatToUsdt(krw, ratePerUsdt)
}

export function formatUsdtAmount (value, { maxDecimals = 4, minDecimals = 2 } = {}) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0'
  return num.toLocaleString('ko-KR', {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals
  })
}

export function formatCurrencyAmount (value, currency = '$') {
  const amount = formatMoney(value)
  const code = String(currency || '$').trim() || '$'
  return `${code}${amount}`
}

export function formatBalance (value, currency = '$') {
  return formatCurrencyAmount(value, currency)
}

/**
 * API UTC 시각 → 브라우저 로컬 Date
 * - 타임존 표기 없는 문자열(예: 2026-05-27 15:25:00)은 UTC로 해석
 */
export function parseUtcToLocalDate (value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'number') {
    const ms = Math.abs(value) < 1e12 ? value * 1000 : value
    const date = new Date(ms)
    return Number.isNaN(date.getTime()) ? null : date
  }

  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null

  if (/^\d+$/.test(trimmed)) {
    const num = Number(trimmed)
    if (!Number.isFinite(num)) return null
    const ms = Math.abs(num) < 1e12 ? num * 1000 : num
    const date = new Date(ms)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const hasTz = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed)
  let normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T')
  normalized = normalized.replace(/(\.\d{3})\d+/, '$1')

  if (!hasTz) {
    normalized = `${normalized}Z`
  }

  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function formatLocalDateParts (date, { withSeconds = false } = {}) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  if (!withSeconds) return `${y}-${m}-${d} ${hh}:${mm}`
  const ss = String(date.getSeconds()).padStart(2, '0')
  return `${y}-${m}-${d} ${hh}:${mm}:${ss}`
}

/** API UTC → 로컬 시간 (기본: 분까지) */
export function formatDateTime (value, { withSeconds = false } = {}) {
  return formatUtcDateTime(value, { withSeconds })
}

/** API UTC → 로컬 시간 표시 */
export function formatUtcDateTime (value, { withSeconds = true } = {}) {
  const date = parseUtcToLocalDate(value)
  if (!date) return '-'
  return formatLocalDateParts(date, { withSeconds })
}

export function displayValue (value, emptyText = '-') {
  if (value === null || value === undefined) return emptyText
  const text = String(value).trim()
  return text || emptyText
}

/** 처리일시 유효 여부 (미처리·기본값 0001-01-01 등 제외) */
export function isValidProcDate (value) {
  if (value === null || value === undefined) return false
  const str = String(value).trim()
  if (!str || str === '0') return false
  if (/^000[01]-/.test(str)) return false
  const date = parseUtcToLocalDate(value)
  if (!date) return false
  return date.getFullYear() >= 1970
}

/** 입·출금 처리일시 (미완료·무효 날짜는 '-') */
export function formatWalletProcDate (value, { pending = false } = {}) {
  if (pending) return '-'
  if (!isValidProcDate(value)) return '-'
  return formatUtcDateTime(value)
}
